/**
 * Rules that relate a request's terms to each other and to the platform's state.
 *
 * The contract enforces some of them at deposit (budget a multiple of 5, a window of at most 90
 * days). Checking them at request time lets the brand fix them before any money moves. The rest
 * are Morcast's rules (specification §4.2, §7.2, §7.3).
 */

import { formatTokenAmount } from "./amounts.js";
import { campaignDayMs, formatCampaignDays, hasShortDays } from "./day.js";
import type { CampaignRequestTerms } from "./terms.js";
import { isZonedMidnight, YOUTUBE_TIME_ZONE } from "./time.js";

// Every duration below counts campaign days (`day.ts`), which are real days except on a test
// network set up with short days.

/** Longest campaign window: the contract's `MAX_CAMPAIGN_DURATION`. */
export const MAX_CAMPAIGN_DAYS = 90;

/**
 * Longest X campaign window. X reports organic impressions only for posts from the last 30 days,
 * so an X campaign must end within 29 days of its start (§4.2).
 */
export const MAX_X_CAMPAIGN_DAYS = 29;

/**
 * Least time between submitting a request and the campaign's start. The admin team reviews the
 * request, and the brand must deposit before the start. Requests starting sooner cannot be run.
 */
export const MIN_LEAD_TIME_DAYS = 3;

/** A campaign token the platform accepts for new campaigns. */
export interface AcceptedToken {
  /** Lowercase address. */
  address: string;
  symbol: string;
  decimals: number;
}

/**
 * Least time between accepting a request and the campaign's start: the brand needs time to make
 * the deposit, which the escrow refuses at or after the start.
 */
export const MIN_ACCEPTANCE_LEAD_TIME_DAYS = 1;

/** The platform's state the rules depend on. */
export interface TermsContext {
  /** The current time. */
  now: Date;
  /** Tokens of the deployment that receives new campaigns. */
  tokens: readonly AcceptedToken[];
  /**
   * Least days between `now` and the start: `MIN_LEAD_TIME_DAYS` for new requests (the default),
   * `MIN_ACCEPTANCE_LEAD_TIME_DAYS` when the admin team accepts one.
   */
  minLeadTimeDays?: number;
}

/**
 * Checks a request's terms against Morcast's rules.
 *
 * @returns Every problem found, as sentences for the brand. Empty when the terms are acceptable.
 */
export function checkCampaignTerms(terms: CampaignRequestTerms, context: TermsContext): string[] {
  const problems: string[] = [];

  if (terms.platform === "X" && terms.format !== null) {
    problems.push("An X campaign has no format.");
  }
  if (terms.platform === "YOUTUBE" && terms.format === null) {
    problems.push("A YouTube campaign needs a format, either DEDICATED or INTEGRATION.");
  }

  const token = context.tokens.find((entry) => entry.address === terms.token);
  if (!token) {
    const offered = context.tokens.map((entry) => entry.symbol).join(" or ");
    problems.push(`The token is not accepted for new campaigns. Use ${offered}.`);
  }
  // The contract splits the spent budget 20/80 in base units, so it requires a multiple of 5.
  if (terms.budget % 5n !== 0n) {
    const step = token
      ? `${formatTokenAmount(5n, token.decimals)} ${token.symbol}`
      : "5 base units";
    problems.push(`The budget must be a multiple of ${step}.`);
  }

  const { startAt, endAt } = terms;
  if (startAt.getUTCMilliseconds() !== 0 || endAt.getUTCMilliseconds() !== 0) {
    problems.push("Start and end times must be whole seconds.");
  }
  if (startAt.getTime() >= endAt.getTime()) {
    problems.push("The campaign must end after it starts.");
  } else {
    const days = (endAt.getTime() - startAt.getTime()) / campaignDayMs();
    if (days > MAX_CAMPAIGN_DAYS) {
      problems.push(`A campaign can last at most ${formatCampaignDays(MAX_CAMPAIGN_DAYS)}.`);
    } else if (terms.platform === "X" && days > MAX_X_CAMPAIGN_DAYS) {
      problems.push(`An X campaign can last at most ${formatCampaignDays(MAX_X_CAMPAIGN_DAYS)}.`);
    }
  }
  const leadDays = context.minLeadTimeDays ?? MIN_LEAD_TIME_DAYS;
  if (startAt.getTime() < context.now.getTime() + leadDays * campaignDayMs()) {
    problems.push(
      leadDays === MIN_LEAD_TIME_DAYS
        ? `The campaign must start at least ${formatCampaignDays(MIN_LEAD_TIME_DAYS)} from now, to leave time for review and the deposit.`
        : `The campaign must start at least ${formatCampaignDays(leadDays)} from now, to leave time for the deposit.`,
    );
  }
  if (terms.platform === "YOUTUBE" && hasShortDays()) {
    problems.push(
      "YouTube reports views by calendar date, so a YouTube campaign needs real days. This test network has short days, so run an X campaign here.",
    );
  }
  if (
    terms.platform === "YOUTUBE" &&
    !(isZonedMidnight(startAt, YOUTUBE_TIME_ZONE) && isZonedMidnight(endAt, YOUTUBE_TIME_ZONE))
  ) {
    problems.push(
      "A YouTube campaign covers whole reporting dates, so it must start and end at 00:00 in Los Angeles.",
    );
  }

  return problems;
}
